import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAdminConfig, updateAdminConfig, fetchGeoBlocks } from '../../lib/adminApi';
import { INCENTIVE_RULES } from '../../lib/incentiveDefaults';
import type { GeoBlocksResponse } from '../../lib/adminApi';

function ConfigRow({ configKey, label, live, currentValue, onSave }: {
  configKey: string; label: string; live: boolean; currentValue: string; onSave: (key: string, value: string) => Promise<void>;
}) {
  const [value, setValue] = useState(currentValue);
  const [saving, setSaving] = useState(false);
  const changed = value !== currentValue;

  async function handleSave() {
    setSaving(true);
    await onSave(configKey, value);
    setSaving(false);
  }

  return (
    <div className="flex items-center justify-between py-3 border-b border-slate-700 last:border-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm text-white font-medium">{label}</p>
          {live ? (
            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-green-500/20 text-green-300 border border-green-500/40">Live</span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/25 text-amber-200 border border-amber-400/60">Display only — not applied</span>
          )}
        </div>
        <p className="text-xs text-slate-500">{configKey}</p>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="bg-slate-700 text-white rounded-lg px-3 py-1.5 text-sm w-32 border border-slate-600 text-right"
        />
        {changed && (
          <button
            onClick={() => void handleSave()}
            disabled={saving}
            className="bg-primary text-white px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
          >
            {saving ? '...' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function AdminConfig() {
  const { data, isLoading } = useQuery({ queryKey: ['admin', 'config'], queryFn: fetchAdminConfig });
  const queryClient = useQueryClient();

  const configs: Record<string, string> = {};
  if (data?.configs) {
    for (const c of data.configs as { key: string; value: string }[]) {
      configs[c.key] = c.value;
    }
  }

  async function handleSave(key: string, value: string) {
    await updateAdminConfig(key, value);
    void queryClient.invalidateQueries({ queryKey: ['admin', 'config'] });
  }

  // `live` = the value is actually read by the platform for new transactions.
  // Only the two payout rates are wired today; the rest are stored but inert.
  const rows = [
    { key: 'SURVEY_PAYOUT_RATE', label: 'Survey Payout Rate (base, L1–L2)', live: true },
    { key: 'ELEVATED_PAYOUT_RATE', label: 'Elevated Payout Rate (L3–L5)', live: true },
    { key: 'SURVEY_PAYOUT_FLOOR', label: 'Min Payout Points (floor)', live: false },
    { key: 'REDEMPTION_MINIMUM_POINTS', label: 'Redemption Minimum', live: false },
    { key: 'POINTS_EXPIRY_DAYS', label: 'Points Expiry Days', live: false },
    { key: 'STREAK_DAILY_BONUS', label: 'Streak Daily Bonus', live: false },
    { key: 'STREAK_MILESTONE_7', label: 'Streak 7-Day Bonus', live: false },
    { key: 'STREAK_MILESTONE_30', label: 'Streak 30-Day Bonus', live: false },
    { key: 'STREAK_MILESTONE_100', label: 'Streak 100-Day Bonus', live: false },
    { key: 'PRESCREEN_CACHE_DAYS', label: 'Prescreen Cache Days', live: false },
    { key: 'INFERRED_EXPIRY_DAYS', label: 'Inferred Expiry Days', live: false },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold font-display text-white">Platform Config</h1>

      <div className="bg-amber-900/30 border border-amber-700/50 rounded-xl px-4 py-3">
        <p className="text-xs text-amber-300">
          Only fields marked <span className="font-semibold text-green-300">Live</span> are read by the platform for new transactions.
          Fields marked <span className="font-semibold text-amber-200">Display only</span> are saved but not yet applied — changing them has no effect.
        </p>
      </div>

      {isLoading ? (
        <div className="text-slate-400">Loading...</div>
      ) : (
        <div className="bg-slate-800 rounded-xl p-5">
          {rows.map((r) => (
            <ConfigRow
              key={r.key}
              configKey={r.key}
              label={r.label}
              live={r.live}
              currentValue={configs[r.key] ?? String((INCENTIVE_RULES as Record<string, number>)[r.key] ?? '')}
              onSave={handleSave}
            />
          ))}
        </div>
      )}

      <GeoBlocksSection />
    </div>
  );
}

function GeoBlocksSection() {
  const [allTime, setAllTime] = useState(false);
  const days = allTime ? 365 : 30;
  const { data, isLoading } = useQuery<GeoBlocksResponse>({
    queryKey: ['admin', 'geo-blocks', days],
    queryFn: () => fetchGeoBlocks(days),
  });

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-lg font-semibold text-white">Blocked Registration Attempts by Country</h2>
        <p className="text-xs text-slate-400 mt-1">
          Non-US visitors who attempted to register. Use this data to inform international expansion decisions.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => setAllTime(false)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${!allTime ? 'bg-primary text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
        >
          Last 30 days
        </button>
        <button
          onClick={() => setAllTime(true)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${allTime ? 'bg-primary text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
        >
          All time
        </button>
      </div>

      {isLoading ? (
        <div className="text-slate-400 text-sm">Loading...</div>
      ) : !data || data.by_country.length === 0 ? (
        <div className="bg-slate-800 rounded-xl p-5 text-center text-slate-500 text-sm">
          No blocked attempts recorded yet
        </div>
      ) : (
        <>
          <div className="bg-slate-800 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left px-4 py-2.5 text-slate-400 font-medium">Country</th>
                  <th className="text-right px-4 py-2.5 text-slate-400 font-medium">Attempts</th>
                  <th className="text-right px-4 py-2.5 text-slate-400 font-medium">% of Total</th>
                </tr>
              </thead>
              <tbody>
                {data.by_country.map((row) => (
                  <tr key={row.country} className="border-b border-slate-700/50 last:border-0">
                    <td className="px-4 py-2 text-white">{row.country}</td>
                    <td className="px-4 py-2 text-right text-slate-300">{row.count.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right text-slate-400">{row.pct.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-500">
            Total blocked attempts: {data.total.toLocaleString()}
          </p>
        </>
      )}
    </div>
  );
}
